/**
 * Average True Range (ATR)
 *
 * Wilder's measure of market volatility.
 * True Range = max(High−Low, |High−PrevClose|, |Low−PrevClose|).
 * ATR = EMA(TR, period) using Wilder smoothing.
 * Default period: 14.
 *
 * Category: Volatility | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const atrIndicator: IndicatorDefinition = {
  id: "atr",
  name: "Average True Range",
  shortName: "ATR",
  category: "volatility",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 1, max: 200 },
    { key: "color", label: "Color", type: "color", default: "#FF9800" },
  ],
  plots: [
    { key: "atr", label: "ATR", color: "#FF9800", style: "line", lineWidth: 2, priceScaleId: "atr" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period } = params;
    const data: { time: number; value: number }[] = [];

    if (candles.length < 2) return [{ key: "atr", data }];

    // True Range array
    const tr: number[] = [candles[0].high - candles[0].low];
    for (let i = 1; i < candles.length; i++) {
      const hl = candles[i].high - candles[i].low;
      const hc = Math.abs(candles[i].high - candles[i - 1].close);
      const lc = Math.abs(candles[i].low - candles[i - 1].close);
      tr.push(Math.max(hl, hc, lc));
    }

    // First ATR = SMA of first `period` TRs
    if (tr.length < period) return [{ key: "atr", data }];

    let atr = 0;
    for (let i = 0; i < period; i++) atr += tr[i];
    atr /= period;
    data.push({ time: candles[period - 1].time, value: atr });

    // Smoothed ATR (Wilder's method)
    for (let i = period; i < tr.length; i++) {
      atr = (atr * (period - 1) + tr[i]) / period;
      data.push({ time: candles[i].time, value: atr });
    }

    return [{ key: "atr", data }];
  },
};

/** Utility: compute ATR array for use by other indicators (e.g. Supertrend) */
export function computeATR(candles: CandleData[], period: number): number[] {
  const result: number[] = new Array(candles.length).fill(NaN);
  if (candles.length < 2) return result;

  const tr: number[] = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const hl = candles[i].high - candles[i].low;
    const hc = Math.abs(candles[i].high - candles[i - 1].close);
    const lc = Math.abs(candles[i].low - candles[i - 1].close);
    tr.push(Math.max(hl, hc, lc));
  }

  if (tr.length < period) return result;

  let atr = 0;
  for (let i = 0; i < period; i++) atr += tr[i];
  atr /= period;
  result[period - 1] = atr;

  for (let i = period; i < tr.length; i++) {
    atr = (atr * (period - 1) + tr[i]) / period;
    result[i] = atr;
  }

  return result;
}
